import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { kebabCase } from 'lodash';

import BlockSection from '~/components/wrappers/BlockSection';

import Row from '~/layout/Row';
import Column from '~/layout/Column';

import Image from '~/components/base/Image';
import Icon from '~/components/base/Icon';

import { CtaLink } from '~/components/base/ArrowCta';

import theme from '~/theme';
import { h2, p } from '~/styles/typography';
import { transition } from '~/styles/transition';

import { formatText } from '~/utilities/helpers';

import {
    GlobalBlockOptions,
    AcfAttributes,
    DefaultGlobalBlockOptions,
    DefaultAcfAttributes,
    AcfLink
} from '~/utilities/propTypes';

const StyledColumn = styled(Column)`
    display: flex;

    @media (min-width: ${theme.breakpoint.tablet}) {
        &:nth-of-type(odd) {
            padding-right: 15px;
        }

        &:nth-of-type(even) {
            padding-left: 15px;
        }
    }
`;

const sharedWrapperStyles = css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.layout.gutter};
    align-items: center;
    justify-content: center;

    @media (min-width: ${theme.breakpoint.largePhone}) {
        flex-wrap: nowrap;
    }

    @media (min-width: ${theme.breakpoint.tablet}) {
        display: flex;
        align-items: flex-end;
    }
`;

const Title = styled.h2`
    ${transition({}, 'color')};
    ${h2}
    color: ${theme.colors.primary.red};
`;

const StyledCtaLink = styled(CtaLink)`
    ${sharedWrapperStyles}

    &:hover,
    &:focus {
        ${Title} {
            color: ${theme.colors.primary.yellow};
        }
    }
`;

const Div = styled.div`
    ${sharedWrapperStyles}
`;

const StyledImage = styled(Image)`
    width: 100%;
    max-width: 300px;
    max-height: 290px;
    align-self: flex-end;

    @media (min-width: ${theme.breakpoint.largePhone}) {
        flex: 1 1 50%;
    }
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    align-items: center;
    text-align: center;

    @media (min-width: ${theme.breakpoint.largePhone}) {
        flex: 1 1 50%;
        align-items: stretch;
        text-align: left;
    }
`;

const Copy = styled.p`
    ${p}
`;

const HalfWidthFeatureCtas = ({
    ctas,
    globalBlockOptions = DefaultGlobalBlockOptions,
    attributes = DefaultAcfAttributes
}) => {
    return ctas?.length ? (
        <BlockSection {...{ globalBlockOptions, attributes }} padding="none">
            <Row
                rowGapTablet="sectionPaddingSmall"
                rowGapPhone="sectionPaddingSmallTablet"
                rowGap="sectionPaddingSmallMobile"
            >
                {ctas.map(({ isLink, link, title, copy, image }) => {
                    const Wrapper = isLink ? StyledCtaLink : Div;
                    return (
                        <StyledColumn
                            spanTablet={6}
                            span={12}
                            key={
                                isLink
                                    ? `${kebabCase(link.title)}-${link.url}`
                                    : kebabCase(title)
                            }
                        >
                            <Wrapper to={isLink ? link.url : ''}>
                                {image ? (
                                    <StyledImage
                                        {...image}
                                        objectFit="contain"
                                    />
                                ) : null}
                                <Content>
                                    <Title>
                                        {isLink
                                            ? formatText(link.title)
                                            : formatText(title)}
                                    </Title>
                                    {copy ? (
                                        <Copy>{formatText(copy)}</Copy>
                                    ) : null}
                                    {isLink ? (
                                        <Icon
                                            icon="circle-arrow"
                                            color="yellow"
                                            size={50}
                                            sizeTablet={40}
                                            sizeMobile={40}
                                            sizeLarge={50}
                                        />
                                    ) : null}
                                </Content>
                            </Wrapper>
                        </StyledColumn>
                    );
                })}
            </Row>
        </BlockSection>
    ) : null;
};

HalfWidthFeatureCtas.propTypes = {
    ctas: PropTypes.arrayOf(
        PropTypes.shape({
            isLink: PropTypes.bool,
            link: AcfLink,
            copy: PropTypes.string,
            title: PropTypes.string,
            image: PropTypes.shape({})
        })
    ).isRequired,
    globalBlockOptions: GlobalBlockOptions,
    attributes: AcfAttributes
};

export default HalfWidthFeatureCtas;
